import { DefaultError, useQuery, useMutation } from "@tanstack/react-query";
import { apiClient } from "utils";
import { useResponseHandler } from "utils/service/responseHandler";
import { IGetPermissionsResponse } from "./types";
import { useQueryToast } from "hooks";
import i18next from "i18next";
import { IUserPermissionQuery } from "./types";

export const useGetPermissions = (userId: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IGetPermissionsResponse, DefaultError>({
    queryKey: ["permissions, userId"],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `UserManagement/GetPermissions`,
        params: { userId },
      });

      return responseHandler(response, {
        groupName: `useGetPermissions`,
      });
    },
  });
};

export const useGetPermissionsFromUser = () => {
  const { responseHandler } = useResponseHandler();

  return useMutation<IGetPermissionsResponse, DefaultError, string>({
    mutationFn: async (userId: string) => {
      const response = await apiClient({
        method: "GET",
        url: `UserManagement/GetPermissions`,
        params: { userId },
      });

      return responseHandler(response, {
        groupName: `useGetPermissions`,
      });
    },
  });
};

export const useUpdatePermissions = (userId: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePortMerch");
  return useMutation<
    IGetPermissionsResponse,
    DefaultError,
    IUserPermissionQuery
  >({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: i18next.t("permissions.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "UserManagement/ManagePermissions",
        body: { userId, ...body },
      });

      return responseHandler(response, {
        groupName: "useUpdatePermissions",
        notFoundPath: "/users",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: i18next.t("permissions.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCheckPermissions = (userId: string) => {
  const { responseHandler } = useResponseHandler();
  
  return useMutation<
    { requiredPermissions: Array<{
      permissionName: string;
      flag: number;
      requiredPermissionName: string;
      requiredFlag: number;
    }> } | null,
    DefaultError,
    IUserPermissionQuery
  >({
    mutationFn: async (body) => {
      try {
        const response = await apiClient({
          method: "POST",
          url: "UserManagement/CheckPermissions",
          body: body.permissions,
        });

        const result = await responseHandler<Array<{
          permissionName: string;
          flag: number;
          requiredPermissionName: string;
          requiredFlag: number;
        }>>(response, {
          groupName: "useCheckPermissions",
          notFoundPath: "/users",
        });

        if (result && Array.isArray(result) && result.length > 0) {
          return {
            requiredPermissions: result
          };
        }

        return null;
      } catch (error) {
        return null;
      }
    },
    onSuccess: (data) => {
    },
    onError: (error) => {
    },
  });
};
